import type { Book, BookId, BookSide, MarketCode } from '@sharpr/types';
import { firstBy, groupBy } from 'remeda';
import { sideEv } from './odds';
import type { Pmf } from './pmf';
import type { PropLine } from './prop';

// A player's PMFs by market.
export type MarketPmfs = Partial<Record<MarketCode, Pmf>>;

// The best-line floor: an exchange side with less liquidity (USD) than `min` is shown but never wins a best line. Only an
// exchange's liquidity counts; a sportsbook's max bet never does.
export interface BestLineFloor { min: number; exchanges: ReadonlySet<BookId> }
export const DEFAULT_BEST_LINE_MIN_LIQUIDITY = 25;
export const NO_BEST_LINE_FLOOR: BestLineFloor = { min: 0, exchanges: new Set() };
export function bestLineFloor(min: number, books: Iterable<{ id: BookId; limitKind: Book['limitKind'] }>): BestLineFloor {
  return { min, exchanges: new Set([...books].filter(b => b.limitKind === 'liquidity').map(b => b.id)) };
}
export function belowBestLineFloor(floor: BestLineFloor, book: BookId, limit: number | undefined): boolean {
  return limit !== undefined && limit < floor.min && floor.exchanges.has(book);
}

// What may win a best line: no excluded book, and no exchange side under the floor. Only the pricers read it; everything
// else reads the best lines they computed, so a row, its overlay and the mobile list can't disagree.
export interface BestLineEligibility { excluded: ReadonlySet<BookId>; floor: BestLineFloor }
export const EVERY_LINE_ELIGIBLE: BestLineEligibility = { excluded: new Set(), floor: NO_BEST_LINE_FLOOR };
export function eligibleForBestLine({ excluded, floor }: BestLineEligibility, book: BookId, limit: number | undefined): boolean {
  return !excluded.has(book) && !belowBestLineFloor(floor, book, limit);
}

// One side of one prop line, with its EV when the market has a PMF.
export interface Pick { propLine: PropLine; side: BookSide; ev?: number }

const priced = (p: Pick): boolean => p.ev !== undefined;
const ev = (p: Pick): number => p.ev ?? 0;
// Unpriced lines rank by the more favorable line: lower for an over, higher for an under.
const favorableLine = (p: Pick): number => (priced(p) ? 0 : p.side === 'over' ? -p.propLine.line : p.propLine.line);

function picks(propLines: readonly PropLine[], pmfs: MarketPmfs, eligibility: BestLineEligibility): Pick[] {
  return propLines.flatMap(propLine => {
    if (propLine.removed) return [];
    const pmf = pmfs[propLine.market];
    return (['over', 'under'] as const).flatMap(side =>
      propLine[side] === undefined || !eligibleForBestLine(eligibility, propLine.book, side === 'over' ? propLine.overLimit : propLine.underLimit)
        ? [] : [{ propLine, side, ev: pmf && sideEv(pmf, propLine, side)?.ev }]);
  });
}

export interface BestLines { over?: Pick; under?: Pick }
export type BestLinesByMarket = Partial<Record<MarketCode, BestLines>>;

// Each market's best over and best under — the line-shopping sense of "best line". Within a side:
// priced first, by EV; unpriced by the more favorable line; then higher odds, then book id, so the
// result is independent of input order. Only an eligible side can win.
export function bestLines(propLines: readonly PropLine[], pmfs: MarketPmfs, eligibility: BestLineEligibility): BestLinesByMarket {
  const out: BestLinesByMarket = {};
  for (const sidePicks of Object.values(groupBy(picks(propLines, pmfs, eligibility), p => `${p.propLine.market}|${p.side}`))) {
    const best = firstBy(sidePicks, [priced, 'desc'], [ev, 'desc'], [favorableLine, 'desc'], [p => p.propLine[p.side]!, 'desc'], p => p.propLine.book);
    out[best.propLine.market] = { ...out[best.propLine.market], [best.side]: best };
  }
  return out;
}

// The headline among a player's best lines: priced before odds-only; then highest EV, positive or not
// (consumers test ev > 0); then market code, and within a market the over. Lines and odds aren't
// comparable across markets.
export function bestMarket(lines: BestLinesByMarket): Pick | undefined {
  const all = Object.values(lines).flatMap(({ over, under }) => [over, under].filter(p => p !== undefined));
  return firstBy(all, [priced, 'desc'], [ev, 'desc'], p => p.propLine.market, [p => p.side === 'over', 'desc']);
}
